import { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import pageBuilderStore from '../../stores/pageBuilderStore';
import { IPreviewMode } from "../../stores/state-interfaces";
import { Button, MenuItem, Select } from "@mui/material";
import {
  Eye,
  Laptop,
  Redo,
  Smartphone,
  Undo,
  Tablet,
  X
} from "lucide-react";
import pagebuilder from "../pagebuilder";

/**
 * PreviewToolbar – Toolbar for Preview Mode Controls in PageBuilder
 *
 * @component
 * @description
 * A fixed horizontal toolbar that provides controls for toggling between Design and Preview modes,
 * selecting device display sizes, and switching between Desktop/Tablet/Phone views. It also includes
 * placeholders for Undo/Redo actions and a Preview "exit" button.
 *
 * @features
 * - Displays in two states: Design Mode and Preview Mode
 * - In Preview Mode:
 *   - Shows device view buttons (Desktop, Tablet, Phone)
 *   - Dropdown to select common screen widths (e.g., 1280px, 768px, 360px)
 *   - Auto-infers device type from selected width
 *   - Close button (X icon) exits preview mode
 * - In Design Mode:
 *   - Placeholder buttons for Undo, Redo
 *   - Static device icons for UI symmetry
 *   - "Eye" icon to enter Preview Mode
 *
 * @state
 * - Local state `currDeviceMode` syncs with `pageBuilderStore.previewMode.deviceMode`
 * - Controlled by MobX store `pageBuilderStore.previewMode` (showPreview, selectedWidth, deviceMode)
 *
 * @remarks
 * - Designed to live at the top of the editor workspace (not reusable outside builder context)
 * - PreviewMode determines whether the toolbar shows preview controls vs. design actions
 * - Currently, Undo/Redo buttons are visual placeholders — logic to be implemented in a future session
 * - Device widths are mapped manually to infer modes; more responsive support may be added later
 *
 * @future
 * - Hook up actual Undo/Redo state stacks
 * - Allow users to preview pages at custom breakpoints
 * - Integrate screen zoom or rotation simulation
 * - Collapse toolbar into floating mode for smaller screens
 *
 * @example
 * <PreviewToolbar />
 *
 * @since 2025-06-11
 * @lastUpdated 2025-06-14
 */
export default observer(function PreviewToolbar () {
	const [currDeviceMode, setCurrDeviceMode] = useState(pageBuilderStore.previewMode.deviceMode);

	const isPreview = pageBuilderStore.previewMode.showPreview;

  const deviceModes = ["desktop", "tablet", "phone"];
  const deviceIcons: Record<string, JSX.Element> = {
    desktop: <Laptop size={24} />,
    tablet: <Tablet size={24} />,
    phone: <Smartphone size={24} />,
  };

  const widths = [
    "1920px", "1440px", "1280px", "1024px", "768px", "360px"
  ];

  if (isPreview) {
    // Preview Mode Toolbar
    return (
      <div className="flex items-center w-full h-12 bg-white shadow border-b z-10">
				{/* Left Spacer */}
				<div className="flex-1" />

				{/* Center: Device Mode Buttons */}
				<div className="flex items-center">
					{deviceModes.map((mode) => (
						<button
							key={mode}
							onClick={() => {
								pageBuilderStore.setPreviewMode({ deviceMode: mode as IPreviewMode["deviceMode"] });
								setCurrDeviceMode(mode as IPreviewMode["deviceMode"]);
								if (mode === "desktop") {
									pageBuilderStore.previewMode.selectedWidth = "1440px";
								} else if (mode === "tablet") {
									pageBuilderStore.previewMode.selectedWidth = "1024px";
								} else if (mode === "phone") {
									pageBuilderStore.previewMode.selectedWidth = "360px";
								}
							}}
							className={`w-12 h-12 flex items-center justify-center border-t border-l
								${
									currDeviceMode === mode
										? "bg-blue-600 text-white"
										: "bg-white text-blue-600 hover:bg-blue-50"
								}`}
						>
							{deviceIcons[mode]}
						</button>
					))}
				</div>

				{/* Width Selection Dropdown */}
				<div>
					<Select
						size="small"
						value={pageBuilderStore.previewMode.selectedWidth || "1280px"}
						onChange={(e) => {
							const width = e.target.value;
							const widthNum = parseInt(width);

							let inferredMode: "desktop" | "tablet" | "phone" = "desktop";
							if (widthNum == 360) inferredMode = "phone";
							else if (widthNum == 1024 || widthNum == 768) inferredMode = "tablet";

							setCurrDeviceMode(inferredMode);

							pageBuilderStore.setPreviewMode({
								selectedWidth: width,
								deviceMode: inferredMode,
							});
						}}
						sx={{
							height: 48,
							fontSize: 14,
							minWidth: 120,
							borderRadius: 0,
							bgcolor: "white",
							'& fieldset': { borderColor: '#ccc' },
						}}
					>
						{widths.map((w) => (
							<MenuItem key={w} value={w}>
								{w}
							</MenuItem>
						))}
					</Select>
				</div>

				{/* Right Spacer */}
				<div className="flex-1" />

				{/* Close Preview Button */}
				<div>
					<Button
						className="w-12 h-12 flex items-center justify-center"
						variant="text"
						onClick={() => pageBuilderStore.setPreviewMode({ showPreview: false })}
					>
						<X size={24} />
					</Button>
				</div>
			</div>
    );
  }

	return (
		<div className="flex items-center h-12 w-full bg-white border-b z-10">
			{/* Left: Undo/Redo */}
			<div className="flex-1 flex items-center">
				<div className="flex-1 flex items-center justify-start">
					<Button 
						title="Undo feature is coming soon!"
						className="w-12 h-12 py-4 items-center justify-center"
						onClick={() => pageBuilderStore.setToast("This feature is under development and will be available soon!")}
					>
						<Undo size={24} />
					</Button>
					<Button 
						title="Redo feature is coming soon!"
						className="w-12 h-12 py-4 items-center justify-center"
						onClick={() => pageBuilderStore.setToast("This feature is under development and will be available soon!")}
					>
						<Redo size={24} />
					</Button>
				</div>
				<div className="flex-1 flex items-center justify-end">
					<Button 
						className="w-12 h-12 py-4 items-center justify-center"
						title="Mobile Responsive Live Editing is coming soon!"
					>
						<Laptop size={24} />
					</Button>
				</div>
			</div>
			{/* Right: Preview */}
			<div className="flex-1 flex items-center">
				<div className="flex-1 flex items-center justify-start">
					<Button 
						className="w-12 h-12 py-4 items-center justify-center"
						title="Mobile Responsive Live Editing is coming soon!"
					>
						<Smartphone size={24} />
					</Button>
				</div>
				<div className="flex-1 flex items-center justify-end">
					<Button 
						className="w-12 h-12 py-4 items-center justify-center"
						onClick={() => pageBuilderStore.setPreviewMode({ showPreview: true })}
					>
						<Eye size={24} />
					</Button>
				</div>
			</div>
		</div>
	);
});
